import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bell, Check, MessageSquare, Users, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { NotificationItem } from '../components/notifications/NotificationItem';
import { useNotifications } from '../components/notifications/useNotifications';

const TABS = [
  { key: 'all', label: 'Tout', icon: Inbox, accent: 'purple' },
  { key: 'general', label: 'Général', icon: Bell, accent: 'purple' },
  { key: 'messages', label: 'Messages', icon: MessageSquare, accent: 'green' },
  { key: 'communities', label: 'Communautés', icon: Users, accent: 'cyan' },
];

/* Brief 27 oct LOT H — « Voir toutes les notifications » pointait vers une route
   inexistante. Page complète, même rendu/logique que la cloche. */
export default function Notifications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'all';
  const [tab, setTab] = useState(initial);
  const category = tab === 'all' ? undefined : tab;
  const { notifications, loading, unreadCount, joinDecisions, load, markAllAsRead, respondJoinRequest, open } = useNotifications({ category });
  const accent = TABS.find((t) => t.key === tab)?.accent || 'purple';

  useEffect(() => { load(); }, [load]);

  const changeTab = (key) => {
    setTab(key);
    setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="notifications-page">
      <div className="flex items-start sm:items-center justify-between gap-4 mb-6 flex-col sm:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bell className="h-6 w-6 text-purple-500" /> Notifications
            {unreadCount > 0 && <span className="text-xs font-semibold bg-purple-500 text-white px-2 py-0.5 rounded-full" data-testid="notifications-page-unread">{unreadCount}</span>}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Réactions, demandes, réservations et activité de tes communautés.</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllAsRead} data-testid="notifications-page-read-all">
            <Check className="h-4 w-4 mr-1" /> Tout marquer comme lu
          </Button>
        )}
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto pb-1" data-testid="notifications-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => changeTab(key)}
            data-testid={`notifications-tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border-2 whitespace-nowrap transition-colors ${
              tab === key
                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-50 dark:divide-gray-700/60">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400" data-testid="notifications-page-empty">
            <Inbox className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-gray-700 dark:text-gray-200">Rien à signaler</p>
            <p className="text-sm mt-1">Tu es à jour. Les nouvelles notifications apparaîtront ici.</p>
          </div>
        ) : notifications.map((n) => (
          <NotificationItem
            key={n.notification_id}
            notification={n}
            accent={accent}
            compact={false}
            onOpen={open}
            joinDecision={joinDecisions[n.notification_id]}
            onRespondJoin={respondJoinRequest}
          />
        ))}
      </div>
    </div>
  );
}
